import React, { Component } from 'react';
import { BlazeTableColDef, BlazeTableOptions } from 'components/blazeTable/BlazeTable.types';
import BlazeTable from 'components/blazeTable/BlazeTable';
import { ApplicationState } from 'store/rootReducer';
import { connect } from 'react-redux';
import i18n from 'i18n';
import { WithTranslation, withTranslation } from 'react-i18next';
import _ from 'lodash';
import {
  changeInspectionViewActivePage,
  changeInspectionViewPageSize,
  setSearchKeyword,
} from 'store/analytics/ui/subViews/inspectionView/inspectionViewActions';
import styles from 'views/Analytics/AnalyticsMockService/Inspection/InspectionViewMar.module.css';
import { ArgumentPair, InspectionViewRequest } from 'types/rest/analyticsTypes';
import {
  fetchInspectionViewRequestDetailThunk,
  fetchInspectionViewRequestsThunk,
} from 'store/analytics/data/analyticsDataActions';
import { InspectionDataState } from 'store/analytics/data/analyticsDataTypes';
import { formatDateTimeFromSeconds } from 'utils/dateUtil';
import AnalyticsRequestDetail from 'views/Analytics/AnalyticsMockService/Inspection/RequestDetail/AnalyticsRequestDetail';
import MixpanelService from 'utils/mixpanelService';
import { REFRESH_INSPECTION_VIEW, SEARCH_FILTER_RESULTS } from 'utils/mixpanelEvents';
import InspectionHeader from 'views/Analytics/AnalyticsMockService/Inspection/Header/InspectionHeader';
import { SV } from 'types/rest/mockServiceTypes';

interface RequestListStateProps {
  requests: InspectionViewRequest[];
}

interface RequestListSDataProps {
  serviceImageFilePath: string | undefined;
}

interface FilteredResults {
  requests: any[];
  tableOptions: BlazeTableOptions;
  sliceData: any;
}

export const _findInArray = (argumentArray: ArgumentPair[], searchKeyword: string) => {
  const exists = argumentArray.find(
    item =>
      (item.key && item.key.toLowerCase().includes(searchKeyword.toLowerCase())) ||
      (item.value && item.value.toLowerCase().includes(searchKeyword.toLowerCase())),
  );
  return exists !== undefined && exists !== null;
};

export const _sliceData = (data: any, activePage: number, pageSize: number) => {
  if (activePage > 0 && data.length > 0) {
    const start = (activePage - 1) * pageSize;
    const end = activePage * pageSize;
    return data.slice(start, end);
  }
  return [];
};

export const getTableOptions = (
  filterResult: any,
  searchKeyword: string,
  tableOptions: BlazeTableOptions,
  pageSize: number,
) => {
  let activePage = 1;
  if (tableOptions.paginationOverride !== undefined) {
    if (tableOptions.paginationOverride.total !== filterResult.length) {
      tableOptions.paginationOverride.activePage = 1;
    }
    activePage = tableOptions.paginationOverride.activePage;
    tableOptions.paginationOverride.total = filterResult.length;
  }
  const sliceData = _sliceData(filterResult, activePage, pageSize);

  return {
    requests: filterResult,
    tableOptions: tableOptions,
    sliceData: sliceData,
  };
};
/**
 * Request List.
 */
export class InspectionViewMar extends Component<AllProps, RequestListStateProps> {
  tableRef: any = React.createRef<BlazeTable>();

  state = {
    requests: [],
  };

  componentDidMount(): void {
    // Gets data from redux to local state
    this.getRequestsFromDataContext();
  }

  componentDidUpdate(prevProps: Readonly<AllProps>) {
    // Do reload of data on change of the tab if needed (= reload timestamp is empty)
    if (
      this.props.activeDataContext &&
      this.props.activeUiContext &&
      this.props.activeUiContext.activeTab === 'inspection' &&
      this.props.activeDataContext.inspection.isLoadingRequests === false &&
      this.props.activeDataContext.inspection.requestsLoadedTs === null &&
      this.props.activeDataContext.inspection.isRequestSent === false
    ) {
      this.props.refreshRequests(this.props.activeTab);
    }

    // If list of requests changes
    if (
      prevProps.activeDataContext &&
      this.props.activeDataContext &&
      prevProps.activeDataContext.inspection.requests !== this.props.activeDataContext.inspection.requests
    ) {
      // Gets data from redux to local state
      this.getRequestsFromDataContext();
    }
  }

  callRefresh = () => {
    MixpanelService.track(REFRESH_INSPECTION_VIEW(this.props.activeTab));
    this.props.refreshRequests(this.props.activeTab);
  };

  /**
   * Renders nested table of elements.
   * @param elements
   */
  generateCondensedTableDom = (elements: string[]) => {
    return (
      <table className="table table-condensed table-striped mb-0">
        {elements.map((item, index) => (
          <tr key={item}>
            <td style={{ borderTop: index === 0 ? 'none' : undefined, paddingLeft: 0 }}>
              <span title={item} className="text-truncate">
                {item}
              </span>
            </td>
          </tr>
        ))}
      </table>
    );
  };

  /**
   * Renders cell with nested table.
   * @param inspectionViewArguments arguments wrapper from the backend. It is key value pair.
   * @param accessor key which will be used for generating list of values
   */
  nestedTableCell = (inspectionViewArguments: ArgumentPair[] | null, accessor: string) => {
    if (_.isNil(inspectionViewArguments) || (inspectionViewArguments && inspectionViewArguments.length === 0)) {
      return null;
    }

    const listOfValues: string[] = inspectionViewArguments.map((item: any) => item[accessor]);
    if (listOfValues.length > 1) {
      return this.generateCondensedTableDom(listOfValues);
    } else {
      return listOfValues[0];
    }
  };

  // Definition of table columns
  colsDefinition: BlazeTableColDef[] = [
    {
      header: i18n.t('analytics:inspection.table.operation'),
      width: 70,
      accessor: 'operation',
    },
    {
      header: i18n.t('analytics:inspection.table.matchType'),
      width: 70,
      accessor: 'matchType',
    },
    {
      header: i18n.t('analytics:inspection.table.matchTolerance'),
      width: 70,
      accessor: 'matchTolerance',
    },
    {
      header: i18n.t('analytics:inspection.table.transactionId'),
      width: 70,
      accessor: 'transactionId',
    },
    {
      header: i18n.t('analytics:inspection.table.timestamp'),
      width: 70,
      accessor: 'timestamp',
      cellComponent: (rowData: InspectionViewRequest) => {
        if (_.isNil(rowData.timestamp)) {
          return 'N/A';
        }
        return formatDateTimeFromSeconds(parseInt(rowData.timestamp) / 1000);
      },
    },
    {
      header: i18n.t('analytics:inspection.table.argumentKey'),
      width: 70,
      cellComponent: (request: InspectionViewRequest) => {
        return this.nestedTableCell(request.arguments, 'key');
      },
      accessor: 'argumentKey',
    },
    {
      header: i18n.t('analytics:inspection.table.argumentValues'),
      width: 70,
      cellComponent: (request: InspectionViewRequest) => {
        return this.nestedTableCell(request.arguments, 'value');
      },
      accessor: 'argumentValues',
    },
  ];

  filterRequestList = (searchKeyword: string) => {
    if (!_.isEmpty(searchKeyword)) {
      return this.state.requests.filter((item: InspectionViewRequest) => {
        return (
          (item.matchTolerance ? item.matchTolerance.toLowerCase().includes(searchKeyword.toLowerCase()) : false) ||
          (item.operation ? item.operation.toLowerCase().includes(searchKeyword.toLowerCase()) : false) ||
          (item.id
            ? item.id
                .toString()
                .toLowerCase()
                .includes(searchKeyword.toLowerCase())
            : false) ||
          (item.timestamp
            ? formatDateTimeFromSeconds(parseInt(item.timestamp) / 1000)
                .toLowerCase()
                .includes(searchKeyword.toLowerCase())
            : false) ||
          (item.transactionId
            ? item.transactionId
                .toString()
                .toLowerCase()
                .includes(searchKeyword.toLowerCase())
            : false) ||
          (item.matchType ? item.matchType.toLowerCase().includes(searchKeyword.toLowerCase()) : false) ||
          _findInArray(item.arguments, searchKeyword)
        );
      });
    } else {
      return this.state.requests;
    }
  };

  handleFilterChange = async (value: string) => {
    await this.props.setSearchKeyword(value);
    this.sendMixPanelEvent();
  };

  sendMixPanelEvent = _.debounce(() => {
    MixpanelService.track(SEARCH_FILTER_RESULTS());
  }, 600);

  /**
   * Gets list of request from active data context in redux.
   */
  getRequestsFromDataContext = () => {
    if (_.isNil(this.props.activeDataContext)) {
      return;
    }

    const inspectionDataState: InspectionDataState = this.props.activeDataContext.inspection;
    this.setState({
      requests: (inspectionDataState.requests as InspectionViewRequest[]) || [],
    });
  };

  render() {
    // UI state
    const activeUiContext = this.props.activeUiContext;
    const activePage = activeUiContext ? activeUiContext.inspectionView.activePage : 1;
    const pageSize = activeUiContext ? activeUiContext.inspectionView.pageSize : 5;
    const selectedRequest = activeUiContext ? activeUiContext.inspectionView.selectedRequest : null;
    const searchKeyword = activeUiContext ? activeUiContext.inspectionView.searchKeyword : '';

    // Data state
    const activeDataContext = this.props.activeDataContext;
    const isRequestListLoading = activeDataContext ? activeDataContext.inspection.isLoadingRequests : false;
    const lastRefresh =
      !_.isNil(activeDataContext) && _.has(activeDataContext, 'inspection.requestsLoadedTs')
        ? new Date(activeDataContext.inspection.requestsLoadedTs)
        : null;
    const tabId = activeDataContext ? activeDataContext.tabId : null;

    const tableOptions: BlazeTableOptions = {
      showRowExpander: false,
      columnDef: this.colsDefinition,
      pageable: true,
      selectable: false,
      pageSize: 5,
      minWidth: 950,
      minHeight: 150,
      tableName: 'requestList',
      noResultsText: i18n.t('analytics:inspection.noResultsText'),
      customHeaderClassName: 'flex-table-item-top',
      customTableClassName: 'flex-table-simple flex-table-condensed',
      onRowClick: (requestId: number) => this.props.fetchInspectionViewRequestDetailThunk(requestId, tabId),
      customRowClassName: (request: any) => {
        let rowClass = 'hand-cursor ';
        if (selectedRequest && request.id === selectedRequest.id) {
          rowClass += styles.customEventClass;
        }
        return rowClass;
      },
      maxContentHeight: '186px',
      paginationOverride: {
        pageSize: pageSize,
        total: this.state.requests.length,
        activePage: activePage,
      },
      hidePager: this.state.requests.length === 0,
    };
    const initResult = this.filterRequestList(searchKeyword);
    const filterResult: FilteredResults = getTableOptions(initResult, searchKeyword, tableOptions, pageSize);

    return (
      <>
        <InspectionHeader
          refreshHandler={this.callRefresh}
          requests={this.state.requests}
          onSearchKeywordChanged={this.handleFilterChange}
          lastRefresh={lastRefresh}
          searchKeyword={searchKeyword}
          title={i18n.t('analytics:inspection.requests')}
          msType={SV}
        ></InspectionHeader>
        <div>
          <BlazeTable
            isLoading={isRequestListLoading}
            options={filterResult.tableOptions}
            onPageSizeChange={this.props.changeInspectionViewPageSize}
            onPageChange={this.props.changeInspectionViewActivePage}
            data={filterResult.sliceData}
            onRef={(ref: any) => (this.tableRef = ref)}
            showPageSizeDropdown={true}
          />
        </div>

        {/* Detail for selected request, show it only if there is detail selected */}
        {!_.isNil(selectedRequest) && (
          <AnalyticsRequestDetail serviceImageFilePath={this.props.serviceImageFilePath || ''} />
        )}
        {/* Detail for selected request */}
      </>
    );
  }
}

const mapStateToProps = ({ analytics }: ApplicationState) => {
  return {
    activeTab: analytics.ui.activeTab,
    activeUiContext: analytics.ui.activeUiContext,
    activeDataContext: analytics.data.activeDataContext,
  };
};

const mapDispatchToProps = {
  changeInspectionViewActivePage,
  changeInspectionViewPageSize,
  fetchInspectionViewRequestDetailThunk,
  setSearchKeyword: setSearchKeyword,
  refreshRequests: fetchInspectionViewRequestsThunk,
};

type MapStateToProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;
type AllProps = MapStateToProps & WithTranslation & DispatchProps & RequestListSDataProps;

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(InspectionViewMar));
